import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Drawer, Typography, Input, Button, Space, Tag, message, Divider, Tooltip, Select } from 'antd';
import { ApiOutlined, GlobalOutlined, LockOutlined, BarChartOutlined, SaveOutlined } from '@ant-design/icons';
import { useStore } from '@/stores';
import { updatePlugin } from '@/api/plugins';
import type { AiPlugin, PluginCategory } from '@/api/types';
import { StatusPill } from '@/components/StatusPill';
import { CategoryChip } from './CategoryChip';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';
import { isGlobalTool, audiencesOf, reachSummary } from './toolMeta';

const { Text, Paragraph } = Typography;

// Right-anchored detail view for one tool. Shows the full description (the
// catalog table truncates it), the tool's global reach, and lets an admin
// edit the description in place. A visualization preview lands here as a
// fast-follow — the placeholder marks the spot so the intent is visible.

interface ToolDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  plugin: AiPlugin | null;
  /** The category vocabulary, for the editable Category select. */
  categories: PluginCategory[];
  /** Called with the saved row so the list can update in place without a refetch. */
  onSaved: (updated: AiPlugin) => void;
}

export const ToolDetailDrawer = observer(function ToolDetailDrawer({
  open,
  onClose,
  plugin,
  categories,
  onSaved,
}: ToolDetailDrawerProps) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  // Reset the editable buffers whenever a different tool is opened.
  useEffect(() => {
    setDescription(plugin?.description ?? '');
    setCategoryId(plugin?.pluginCategoryId ?? null);
  }, [plugin]);

  if (!plugin) return null;

  const global = isGlobalTool(plugin);
  const audiences = audiencesOf(plugin);
  const dirty =
    (description ?? '') !== (plugin.description ?? '') ||
    (categoryId ?? null) !== (plugin.pluginCategoryId ?? null);

  async function handleSave() {
    if (!plugin || !dirty) return;
    setSaving(true);
    try {
      // Send the WHOLE row so the backend's whole-entity update does not null
      // Name / Title / UserTypes — only description + category changed.
      const saved = await updatePlugin({ ...plugin, description, pluginCategoryId: categoryId });
      onSaved(saved);
      message.success(`Saved “${plugin.name}”.`);
      trackEvent('Tool.Saved', { pluginId: plugin.id });
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to save the tool');
    } finally {
      setSaving(false);
    }
  }

  const labelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: colors.slate[500],
    marginBottom: 6,
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width="42%"
      destroyOnClose
      title={
        <Space size={10} align="center">
          <ApiOutlined style={{ color: colors.violet[600], fontSize: 18 }} />
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 15, fontWeight: 600 }}>
            {plugin.name}
          </span>
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
            #{plugin.id}
          </Text>
        </Space>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* ── Global reach ─────────────────────────────────────────────── */}
        <div>
          <div style={labelStyle}>Reach</div>
          <Space size={8} align="center" wrap>
            {global ? (
              <StatusPill tone="amber">
                <GlobalOutlined style={{ marginRight: 4 }} />
                Global
              </StatusPill>
            ) : (
              <StatusPill tone="slate">
                <LockOutlined style={{ marginRight: 4 }} />
                Agent-scoped
              </StatusPill>
            )}
            {audiences.map((a) => (
              <Tag key={a} color={global ? 'gold' : 'default'} style={{ fontSize: 11 }}>
                {a}
              </Tag>
            ))}
          </Space>
          <Paragraph type="secondary" style={{ fontSize: 12.5, marginTop: 8, marginBottom: 0 }}>
            {reachSummary(plugin)}
          </Paragraph>
        </div>

        {/* ── Title ────────────────────────────────────────────────────── */}
        {plugin.title && (
          <div>
            <div style={labelStyle}>Title</div>
            <Text style={{ fontSize: 13.5 }}>{plugin.title}</Text>
          </div>
        )}

        {/* ── Editable category ────────────────────────────────────────── */}
        <div>
          <div style={labelStyle}>Category</div>
          <Space size={10} align="center" wrap>
            <Select<number | null>
              value={categoryId}
              onChange={setCategoryId}
              style={{ minWidth: 260 }}
              placeholder="Uncategorized"
              allowClear
              options={categories.map((c) => ({ label: c.name, value: c.id }))}
            />
            <CategoryChip category={categoryId != null ? categories.find((c) => c.id === categoryId) : null} />
          </Space>
          <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 6, marginBottom: 0 }}>
            The tool’s logical group — how it’s organized and filtered in the catalog and the
            per-agent grant picker. Separate from Reach above.
          </Paragraph>
        </div>

        {/* ── Editable description ──────────────────────────────────────── */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={labelStyle}>Description</div>
            <Tooltip title="This text is what the model reads to decide when to call the tool. Write it for the LLM: what it does, and when to use it.">
              <Text type="secondary" style={{ fontSize: 11, cursor: 'help' }}>
                shown to the model
              </Text>
            </Tooltip>
          </div>
          <Input.TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            autoSize={{ minRows: 5, maxRows: 16 }}
            placeholder="Describe what this tool does and when an agent should call it…"
            style={{ fontSize: 13, lineHeight: 1.6 }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              disabled={!dirty}
              onClick={() => void handleSave()}
            >
              {dirty ? 'Save changes' : 'Saved'}
            </Button>
          </div>
        </div>

        <Divider style={{ margin: '2px 0' }} />

        {/* ── Visualization preview (deferred fast-follow) ─────────────── */}
        <div
          style={{
            border: `1px dashed ${isDark ? colors.slate[600] : colors.slate[300]}`,
            borderRadius: 10,
            padding: '18px 16px',
            textAlign: 'center',
            background: isDark ? `${colors.slate[800]}` : colors.slate[50],
          }}
        >
          <BarChartOutlined style={{ fontSize: 22, color: colors.slate[400] }} />
          <div style={{ fontSize: 13, fontWeight: 600, marginTop: 6, color: isDark ? colors.slate[200] : colors.slate[700] }}>
            Visualization preview
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            For tools that render a chart or card, a live sample will appear here.
            Coming as a fast-follow.
          </Text>
        </div>
      </div>
    </Drawer>
  );
});
